import { createReducer } from '@reduxjs/toolkit';
import { PurchaseOrderDataDto, PurchaseOrderDto } from '../../api/ClientApi';

import {
    savePurchaseOrder,
    importPurchaseOrders,
    addGoodsToPurchaseOrder,
    removeGoodsFromPurchaseOrder,
    addOrUpdatePurchaseOrder,
    changePurchaseOrderStatus,
} from '../actions/purchaseOrderActions';

export interface PurchaseOrdersState {
    purchaseOrders: PurchaseOrderDto[] | null;
    purchaseOrdersInfoDate: Date | null;
    purchaseOrdersFetchActive: boolean;
}

export const purchaseOrdersReducer = createReducer<PurchaseOrdersState>(
    { purchaseOrders: null, purchaseOrdersInfoDate: null, purchaseOrdersFetchActive: false },
    (builder) => {
        builder
            .addCase(addGoodsToPurchaseOrder, (state: PurchaseOrdersState, action: any) => {
                const { purchaseOrderId, purchaseOrderGoods } = action.payload;

                if (!purchaseOrderGoods || !state.purchaseOrders) {
                    return;
                }

                const purchaseOrderIndex = state.purchaseOrders?.findIndex((o) => o.id === purchaseOrderId);
                const purchaseOrderGoodsId = purchaseOrderGoods.id;

                if (purchaseOrderIndex !== undefined && purchaseOrderIndex >= 0) {
                    const oldPurchaseOrder = state.purchaseOrders[purchaseOrderIndex];

                    const oldPurchaseOrderGoodsIndex = oldPurchaseOrder.goods?.findIndex(
                        (ogi) => ogi.id === purchaseOrderGoodsId,
                    );

                    if (oldPurchaseOrderGoodsIndex !== undefined && oldPurchaseOrderGoodsIndex >= 0) {
                        oldPurchaseOrder.goods?.splice(oldPurchaseOrderGoodsIndex, 1, purchaseOrderGoods);
                    } else {
                        if (!oldPurchaseOrder.goods) {
                            oldPurchaseOrder.goods = [purchaseOrderGoods];
                        } else {
                            oldPurchaseOrder.goods.unshift(purchaseOrderGoods);
                        }
                    }

                    state.purchaseOrders.splice(purchaseOrderIndex, 1, oldPurchaseOrder);
                }
            })
            .addCase(removeGoodsFromPurchaseOrder, (state: PurchaseOrdersState, action: any) => {
                const { purchaseOrderId, purchaseOrderGoodsId } = action.payload;

                if (!purchaseOrderGoodsId || purchaseOrderGoodsId <= 0 || !state.purchaseOrders) {
                    return;
                }

                const purchaseOrderIndex = state.purchaseOrders?.findIndex((o) => o.id === purchaseOrderId);

                if (purchaseOrderIndex !== undefined && purchaseOrderIndex >= 0) {
                    const oldPurchaseOrder = state.purchaseOrders[purchaseOrderIndex];

                    const oldPurchaseOrderGoodsIndex = oldPurchaseOrder.goods?.findIndex(
                        (ogi) => ogi.id === purchaseOrderGoodsId,
                    );

                    if (oldPurchaseOrderGoodsIndex !== undefined && oldPurchaseOrderGoodsIndex >= 0) {
                        oldPurchaseOrder.goods?.splice(oldPurchaseOrderGoodsIndex, 1);

                        state.purchaseOrders.splice(purchaseOrderIndex, 1, oldPurchaseOrder);
                    }
                }
            })
            .addCase(savePurchaseOrder.fulfilled, (state: PurchaseOrdersState, action: any) => {
                if (state.purchaseOrders === null) {
                    return;
                }

                const payload: PurchaseOrderDto = action.payload;

                const purchaseOrder: PurchaseOrderDto = new PurchaseOrderDto();
                purchaseOrder.init(payload);

                const purchaseOrderIndex = state.purchaseOrders!.findIndex((a) => {
                    return a.id === purchaseOrder.id;
                });

                if (purchaseOrderIndex >= 0) {
                    // update purchaseOrder
                    state.purchaseOrders.splice(purchaseOrderIndex, 1, purchaseOrder);
                } else {
                    // add purchaseOrder
                    state.purchaseOrders.unshift(purchaseOrder);
                }
            })
            .addCase(changePurchaseOrderStatus.fulfilled, (state: PurchaseOrdersState, action: any) => {
                if (state.purchaseOrders === null) {
                    return;
                }

                const payload: PurchaseOrderDto = action.payload;

                const purchaseOrder: PurchaseOrderDto = new PurchaseOrderDto();
                purchaseOrder.init(payload);

                const purchaseOrderIndex = state.purchaseOrders!.findIndex((a) => {
                    return a.id === purchaseOrder.id;
                });

                if (purchaseOrderIndex >= 0) {
                    // update purchaseOrder
                    state.purchaseOrders.splice(purchaseOrderIndex, 1, purchaseOrder);
                }
            })
            .addCase(addOrUpdatePurchaseOrder, (state: PurchaseOrdersState, action: any) => {
                const newPurchaseOrder = action.payload;

                const oldPurchaseOrderIndex = state.purchaseOrders?.findIndex((o) => o.id === newPurchaseOrder.id);

                if (oldPurchaseOrderIndex !== undefined && oldPurchaseOrderIndex >= 0) {
                    state.purchaseOrders?.splice(oldPurchaseOrderIndex, 1, newPurchaseOrder);
                } else {
                    if (!state.purchaseOrders) {
                        state.purchaseOrders = [newPurchaseOrder];
                    } else {
                        state.purchaseOrders.unshift(newPurchaseOrder);
                    }
                }
            })
            .addCase(importPurchaseOrders.fulfilled, (state: PurchaseOrdersState, action: any) => {
                const result: PurchaseOrderDataDto = action.payload;

                const newPurchaseOrders = result.orders;

                state.purchaseOrders = newPurchaseOrders?.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)) ?? null;
                state.purchaseOrdersInfoDate = result.infoDate ?? null;

                state.purchaseOrdersFetchActive = false;
            })
            .addCase(importPurchaseOrders.pending, (state: PurchaseOrdersState, action: any) => {
                state.purchaseOrdersFetchActive = true;
            })
            .addCase(importPurchaseOrders.rejected, (state: PurchaseOrdersState, action: any) => {
                state.purchaseOrdersFetchActive = false;
            });
    },
);
